import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { getCurrencySymbol, getSymbol } from '../utils/currency';
import { CheckCircle, Warning, ArrowLeft, House, CalendarBlank, Clock, MapPin } from '@phosphor-icons/react';
import { format } from 'date-fns';
import AddToCalendar from '../components/AddToCalendar';
import SellerPaymentOptions from '../components/SellerPaymentOptions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState('checking');
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
      setError('No session ID provided');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      setError('Payment verification timed out. Please check your bookings.');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Fetch booking details
        const bookingRes = await axios.get(`${API_URL}/api/bookings/${response.data.booking_id}`);
        setBooking(bookingRes.data);
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (err) {
      const httpStatus = err.response?.status;
      // Treat any client-side terminal error (404 unknown session, 410 gone, 422 invalid) as final — stop polling.
      if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
        setStatus('error');
        if (httpStatus === 404) {
          setError('We could not find this payment session. It may have expired or never been created.');
        } else if (httpStatus === 401 || httpStatus === 403) {
          setError('You are not authorised to view this payment. Please sign in with the account that made the booking.');
        } else {
          setError(err.response?.data?.detail || 'This payment session is no longer valid.');
        }
        return;
      }
      // 5xx / network — retry, then give up
      console.error('Payment status check error:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('error');
        setError('Failed to verify payment status. Please check your bookings shortly.');
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Verifying Payment...</h2>
          <p className="text-slate-500">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'timeout') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center shadow-lg">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Warning size={40} className="text-amber-600" weight="fill" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Issue</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/bookings')}
              className="w-full bg-blue-500 hover:bg-blue-600 rounded-xl h-12"
            >
              Check My Bookings
            </Button>
            <Button 
              onClick={() => navigate('/buyer')}
              variant="outline"
              className="w-full rounded-xl h-12"
            >
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center shadow-lg">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={48} className="text-emerald-600" weight="fill" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h2>
        <p className="text-slate-600 mb-6">Your booking has been confirmed</p>

        {booking && (
          <div className="bg-slate-50 rounded-xl p-4 text-left mb-6">
            <h3 className="font-semibold text-slate-900 mb-3">{booking.listing_title}</h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <CalendarBlank size={16} />
                <span>{format(new Date(booking.start_date), 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Clock size={16} />
                <span>{booking.start_time} - {booking.end_time}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin size={16} />
                <span className="line-clamp-1">{booking.listing_address}</span>
              </div>
            </div>

            {booking.check_in_code && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <p className="text-xs text-blue-600 mb-1">Check-in Code</p>
                <p className="text-2xl font-bold text-blue-700 tracking-wider">{booking.check_in_code}</p>
              </div>
            )}

            <AddToCalendar
              icsPath={`/api/bookings/${booking.booking_id}/calendar.ics`}
              title={`Booking: ${booking.listing_title || 'Space'}`}
              location={booking.listing_address || ''}
              description={`Booking ID: ${booking.booking_id}\nCheck-in code: ${booking.check_in_code || ''}`}
              startISO={`${booking.start_date}T${booking.start_time || '00:00'}:00`}
              endISO={`${booking.end_date || booking.start_date}T${booking.end_time || booking.start_time || '00:00'}:00`}
              reminderText="A reminder will fire 1 hour before your booking."
            />

            {booking.listing_id && (
              <div className="mt-3">
                <SellerPaymentOptions listingId={booking.listing_id} />
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between">
              <span className="text-slate-600">Total Paid</span>
              <span className="text-lg font-bold text-slate-900">{getSymbol(booking.currency || booking.country)}{booking.total_price}</span>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Button 
            onClick={() => navigate('/bookings')}
            className="w-full bg-blue-500 hover:bg-blue-600 rounded-xl h-12"
            data-testid="view-bookings-btn"
          >
            View My Bookings
          </Button>
          <Button 
            onClick={() => navigate('/buyer')}
            variant="outline"
            className="w-full rounded-xl h-12"
          >
            <House size={18} className="mr-2" />
            Continue Exploring
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
